import { useEffect, useMemo, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

function formatDollar(cents) {
  return "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// Approximate on-brand hex mapping for the color swatch dot.
const COLOR_SWATCH = {
  "Black": "#111111",
  "Vintage Black": "#1a1a1a",
  "White": "#F8F8F8",
  "Solid White Blend": "#F8F8F8",
  "Red": "#C41E3A",
  "Team Red": "#C41E3A",
  "Heather Red": "#B23A48",
  "Royal Blue": "#1E4FBF",
  "True Royal": "#1E4FBF",
  "Team Royal": "#1E4FBF",
  "Heather True Royal": "#3A5FBF",
};
const swatchColor = (c) => COLOR_SWATCH[c] || "#555";
const isDarkGarment = (c) => (c || "").toLowerCase() !== "white" && !c?.toLowerCase().includes("white blend");

export default function BuyMerchModal({ open, onClose, item }) {
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const availableColors = item?.available_colors || [];
  const sizesByColor = item?.sizes_by_color || {};
  const hasColors = availableColors.length > 0;
  const hasMultiColor = availableColors.length > 1;

  // Effective sizes for the currently selected color (falls back to item.sizes for legacy items)
  const currentSizes = useMemo(() => {
    if (hasColors && color && sizesByColor[color]) return sizesByColor[color];
    return item?.sizes || [];
  }, [color, item, hasColors, sizesByColor]);

  useEffect(() => {
    if (open && item) {
      const initialColor = item.available_colors?.[0] || null;
      setColor(initialColor);
      const initialSizes = initialColor && item.sizes_by_color?.[initialColor]
        ? item.sizes_by_color[initialColor]
        : item.sizes || [];
      setSize(initialSizes[0] || null);
      setQuantity(1);
      setEmail("");
      setSubmitting(false);
    }
  }, [open, item]);

  // If the user switches color, snap size to first available for that color
  useEffect(() => {
    if (!hasColors || !color) return;
    const sizesForColor = sizesByColor[color] || [];
    if (sizesForColor.length && !sizesForColor.includes(size)) {
      setSize(sizesForColor[0]);
    }
  }, [color, hasColors, sizesByColor, size]);

  if (!open || !item) return null;

  const total = (item.price_cents || 0) * quantity;

  const checkout = async () => {
    if (hasMultiColor && !color) {
      toast.error("Choose a color first");
      return;
    }
    if (currentSizes.length > 0 && !size) {
      toast.error("Choose a size first");
      return;
    }
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/merchandise/checkout", {
        item_id: item.id,
        quantity,
        size,
        color,
        email,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Payment initiation failed");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="buy-merch-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-4xl bg-bdu-surface border border-bdu-gold/30 my-8 grid md:grid-cols-2 gap-0" role="dialog" aria-modal="true">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-bdu-cream/60 hover:text-bdu-gold transition-colors z-10"
          data-testid="buy-merch-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Image */}
        <div className="relative min-h-[280px] md:min-h-[500px] bg-bdu-bg">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-bdu-cream/30 text-xs uppercase tracking-widest">No image</div>
          )}
        </div>

        {/* Content */}
        <div className="p-8 sm:p-10 flex flex-col">
          <div className="label-eyebrow">{item.category === "tshirt" ? "Apparel" : item.category === "hat" ? "Headwear" : "Merch"}</div>
          <h2 className="mt-3 font-serif text-3xl text-bdu-cream tracking-tight leading-tight">{item.name}</h2>
          <div className="mt-3 font-serif text-3xl text-bdu-gold tabular-nums">{formatDollar(item.price_cents)}</div>
          <p className="mt-5 text-sm text-bdu-cream/70 leading-relaxed">{item.description}</p>

          {/* Colors */}
          {hasMultiColor && (
            <div className="mt-6">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <span>Color</span>
                {color && <span className="text-bdu-cream/50 normal-case tracking-normal text-xs">— {color}</span>}
              </div>
              <div className="flex flex-wrap gap-3" data-testid="merch-color-picker">
                {availableColors.map((c) => {
                  const active = color === c;
                  const hex = swatchColor(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      title={c}
                      aria-label={`Select ${c}`}
                      className={`relative w-10 h-10 border-2 rounded-full transition ${active ? "border-bdu-gold shadow-[0_0_0_2px_rgba(212,175,55,0.15)]" : "border-bdu-cream/20 hover:border-bdu-cream/60"}`}
                      data-testid={`merch-color-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <span
                        className="absolute inset-1 rounded-full"
                        style={{ backgroundColor: hex, boxShadow: hex === "#F8F8F8" ? "inset 0 0 0 1px rgba(0,0,0,0.15)" : "none" }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sizes */}
          {currentSizes.length > 0 && (
            <div className="mt-6">
              <div className="label-eyebrow mb-3">Size</div>
              <div className="flex flex-wrap gap-2" data-testid="merch-size-picker">
                {currentSizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    className={`min-w-[3rem] py-2.5 border text-sm font-semibold tracking-widest ${
                      size === s
                        ? "border-bdu-gold text-bdu-gold bg-bdu-gold/[0.06]"
                        : "border-bdu-gold/25 text-bdu-cream/80 hover:border-bdu-gold/60"
                    }`}
                    data-testid={`merch-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + email */}
          <div className="mt-6 grid grid-cols-[auto_1fr] gap-4 items-end">
            <div>
              <div className="label-eyebrow mb-3">Qty</div>
              <div className="flex items-center border border-bdu-gold/25">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-2 text-bdu-cream/70 hover:text-bdu-gold"
                  data-testid="merch-qty-decrease"
                >−</button>
                <div className="px-4 font-serif text-lg text-bdu-cream min-w-[2rem] text-center" data-testid="merch-qty">{quantity}</div>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(25, quantity + 1))}
                  className="px-3 py-2 text-bdu-cream/70 hover:text-bdu-gold"
                  data-testid="merch-qty-increase"
                >+</button>
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Email for Confirmation</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent border border-bdu-gold/25 px-3 py-2 text-bdu-cream outline-none focus:border-bdu-gold"
                data-testid="merch-email-input"
              />
            </div>
          </div>

          {/* Disclosure */}
          <div className="mt-5 border border-bdu-gold/20 bg-black/40 px-3 py-2 text-[0.7rem] text-bdu-cream/60" data-testid="merch-disclosure">
            Proceeds benefit the Trillion Dollar Spending Power Fund. Purchase is not tax-deductible. Ships to US & Canada; sales tax + shipping added at checkout.
          </div>

          {/* Total + checkout */}
          <div className="mt-auto pt-6 flex items-center justify-between gap-4">
            <div>
              <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Total</div>
              <div className="mt-1 font-serif text-3xl text-bdu-gold tabular-nums" data-testid="merch-total">{formatDollar(total)}</div>
            </div>
            <button
              onClick={checkout}
              disabled={submitting}
              className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="merch-checkout-btn"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : "Continue to Checkout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
